import { watchImmediate } from '@vueuse/core';
import {
    DoubleSide,
    type Material,
    Mesh,
    MeshBasicMaterial,
    PlaneGeometry,
} from 'three';
import { stopAll, type StopHandle } from '@/util';
import { objectNode, type ObjectNode, updateObject } from './object';
import type { SceneContext } from '@/lib/threeScene/SceneContext';
import type { NodeId } from '@/lib/threeScene/nodeId';


export type ImagePlaneNode = ObjectNode<'image-plane'> & {
    material: Material | null;
    width: number;
    height: number;
};

export function imagePlaneNode(id: NodeId, properties?: Partial<ImagePlaneNode>): ImagePlaneNode {
    return {
        ...objectNode('image-plane', id, properties),
        material: properties?.material ?? null,
        width: properties?.width ?? 0,
        height: properties?.height ?? 0,
    };
}

export function updateImagePlane(context: SceneContext, node: ImagePlaneNode): StopHandle {
    const mesh = new Mesh();
    let geometry: PlaneGeometry | null = null;

    const disposeGeometry = () => {
        if (geometry) {
            geometry.dispose();
            geometry = null;
        }
    };

    return stopAll(
        updateObject(context, node, mesh),
        watchImmediate(
            () => [node.width, node.height],
            ([width, height]) => {
                disposeGeometry();
                if (width > 0 && height > 0) {
                    geometry = new PlaneGeometry(width, height);
                    mesh.geometry = geometry;
                }
            },
        ),
        watchImmediate(
            () => node.material,
            (material) => mesh.material = material ?? _defaultMaterial(),
        ),
        disposeGeometry,
    );
}

let _defaultMaterialInstance: MeshBasicMaterial | null = null;

function _defaultMaterial(): MeshBasicMaterial {
    if (_defaultMaterialInstance === null) {
        _defaultMaterialInstance = new MeshBasicMaterial({ side: DoubleSide });
    }
    return _defaultMaterialInstance;
}
